import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { TourContext } from './TourProvider';
import type { TourContextValue, TourName } from './types';

export const useTour = (): TourContextValue => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

// Hook to auto-start a tour if it hasn't been completed
export const useAutoStartTour = (tourName: TourName, delay = 500) => {
  const { startTour, isTourCompleted, state } = useTour();
  const location = useLocation();

  useEffect(() => {
    // Don't auto-start if already running a tour
    if (state.isRunning) {
      return;
    }

    // Don't auto-start if tour is already completed
    if (isTourCompleted(tourName)) {
      return;
    }

    // Delay to allow page components to render
    const timer = setTimeout(() => {
      startTour(tourName);
    }, delay);

    return () => clearTimeout(timer);
  }, [
    location.pathname,
    state.isRunning,
    isTourCompleted,
    tourName,
    startTour,
    delay,
  ]);
};

// Hook to get tour-related utilities for a specific page
export const usePageTour = (tourName: TourName) => {
  const tour = useTour();

  const isCurrentTourActive = tour.state.activeTour === tourName;
  const isCompleted = tour.isTourCompleted(tourName);

  const restart = () => {
    tour.resetTour(tourName);
    tour.startTour(tourName);
  };

  return {
    ...tour,
    isCurrentTourActive,
    isCompleted,
    restart,
    start: () => tour.startTour(tourName),
  };
};
